import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Link } from "wouter";
import { CleaningNotificationHistory, useCleaningConfirmation } from "@/components/cleaning-message-confirmation";
import { useQuery, useMutation, useInfiniteQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { annotateAttachmentUrl } from "@/lib/attachment-url";
import { recordCannedUsesIfPresent, type PendingCannedUse } from "@/lib/canned-response-usage";
import { useAttachmentRateLimit, formatRateLimitCountdown } from "@/hooks/use-attachment-rate-limit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { CannedResponsePicker } from "@/components/canned-response-picker";
import { deriveShortAddress } from "@/lib/message-placeholders";
import { AlertCircle, ArrowLeft, Check, CheckCircle, ChevronDown, ChevronUp, Clock, Filter, FileText, Image as ImageIcon, Loader2, PanelLeftOpen, PanelRightClose, PanelRightOpen, Paperclip, Send, Sparkles, X } from "lucide-react";
import { QuoCallButton } from "./QuoCallButton";
import type { TeamMember, CannedResponse } from "@shared/schema";
import { bucketMemberStatus } from "@shared/member-status";
import { ALLOWED_ATTACHMENT_MIME, ATTACHMENT_MAX_BYTES, ATTACHMENT_MAX_COUNT, MemberSourceBadge, PendingAttachment, TIMELINE_POLL_INTERVAL_MS, TimelineChat, avatarColor, detectCannedTriggerToken, formatBytes, getPadsplitProfileUrl, initials, makeAttachmentId, substituteCannedVars, useCannedTrigger } from "./_shared";
import {
  UNRESOLVED_Q_KEY,
  useResolveInvalidations,
  applyOptimisticInboxResolve,
  rollbackOptimisticInboxResolve,
  resolveAllInboxRowUpdate,
  incrementInboxRowUpdate,
} from "./_resolve-invalidations";
import { ChatBubble } from "./ChatBubble";
import { AutoDraftComposerContext, type AutoDraftComposerApi } from "./auto-draft-composer-context";
import { ReportedTaskCard } from "./ReportedTaskCard";
import { CleaningProofCard } from "./CleaningProofCard";
import type { ReportedTaskSummary, CleaningSubmissionSummary } from "@shared/schema";
import { MessagingOutageBanner } from "@/components/messaging-outage-banner";
import { useMessagingOutage } from "@/hooks/use-messaging-outage";
import { PresenceIndicator, type PresencePeer } from "./PresenceIndicator";
import { uploadMimeType } from "@/lib/upload-mime";



// ─── Member Timeline ──────────────────────────────────────────────────────────
export function MemberTimeline({
  memberId,
  memberName,
  memberPadsplitId,
  propertyName,
  focusChatId,
  focusThreadId,
  focusPendingId,
  canSend,
  onSent,
  toast,
  sidebarOpen,
  onToggleSidebar,
  toggleSidebarButtonRef,
  inboxListOpen,
  onToggleInboxList,
  toggleInboxListButtonRef,
  onBack,
  searchWords = [],
  activeSearchTerm = "",
  searchSinceMs,
}: {
  memberId: number;
  memberName: string;
  memberPadsplitId: string | null;
  propertyName?: string | null;
  // Chat id to scroll to + briefly highlight when arriving from a deep link
  // (dashboard notification → /app/messages?member=N&chat=M).
  focusChatId: number | null;
  // Thread note id to scroll to + highlight (deep link → ?thread=N).
  focusThreadId: number | null;
  // PadSplit send-UX — pending (messages_pending) id to scroll to + highlight
  // when arriving from a failed-send surface (?pending=P). Pending rows are
  // always loaded (prepended), so no infinite-query page walk is needed.
  // Optional so existing callers/tests that don't deep-link a pending row
  // needn't thread it through.
  focusPendingId?: number | null;
  canSend: boolean;
  onSent: () => void;
  toast: ReturnType<typeof useToast>["toast"];
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  toggleSidebarButtonRef?: React.RefObject<HTMLButtonElement>;
  inboxListOpen: boolean;
  onToggleInboxList: () => void;
  toggleInboxListButtonRef?: React.RefObject<HTMLButtonElement>;
  // Mobile master-detail: clears the active member so the conversation list
  // takes over the screen again. Rendered as a back arrow on small viewports.
  onBack?: () => void;
  // Task #2296 — inbox search words/term forwarded so the timeline can
  // highlight every matching bubble and show the match navigator.
  searchWords?: string[];
  activeSearchTerm?: string;
  // Epoch-ms cutoff for the match navigator: only messages at or after this
  // timestamp qualify as matches (mirrors the server-side time-window filter).
  searchSinceMs?: number;
}) {
  const queryClient = useQueryClient();
  const teamQuery = useQuery<TeamMember[]>({ queryKey: ["/api/app/team-members"] });
  const profileQuery = useQuery<{ firstName?: string | null; lastName?: string | null; email?: string | null }>({ queryKey: ["/api/app/profile"] });
  const senderName = [profileQuery.data?.firstName, profileQuery.data?.lastName].filter(Boolean).join(" ").trim() || profileQuery.data?.email || "You";
  const teamMembers = teamQuery.data ?? [];
  // Member room + active property address, used to resolve the
  // {{property_full_address}}, {{property_short_address}}, and {{room_number}}
  // placeholders for canned responses inserted in this timeline.
  const memberDetailQuery = useQuery<{
    roomNumber?: string | null;
    currentPropertyId?: number | null;
    name?: string | null;
    displayName?: string | null;
    padsplitMemberId?: string | null;
    occupantId?: string | null;
    padsplitProfileUrl?: string | null;
    status?: string | null;
    // Merged-stay handling — set when this stay was merged into another
    // (room transfer / duplicate); the conversation lives on that row.
    mergedIntoMemberId?: number | null;
    moveInDate?: string | null;
    moveOutDate?: string | null;
    weeklyRate?: string | number | null;
  }>({
    queryKey: ["/api/app/members", memberId],
  });
  const propertiesQuery = useQuery<Array<{ id: number; address?: string | null }>>({
    queryKey: ["/api/app/properties"],
  });
  // Maintenance tasks this member reported (PadSplit-synced tasks arrive
  // stamped with their reporting member). Surfaced as inline cards interleaved
  // into the conversation so a teammate sees what the member submitted and can
  // jump to the task. Small per-member set, so no pagination.
  const reportedTasksQuery = useQuery<ReportedTaskSummary[]>({
    queryKey: ["/api/app/members", memberId, "reported-tasks"],
  });

  // Member Cleaning System (Build 2) — the member's submitted cleaning proofs,
  // interleaved as inline cards exactly like reported-task cards.
  const cleaningProofsQuery = useQuery<CleaningSubmissionSummary[]>({
    queryKey: ["/api/app/members", memberId, "cleaning-submissions"],
  });
  // Draft persistence — fetch the server-persisted draft for this member.
  // staleTime:0 + refetchOnMount:"always" ensures each member open fetches
  // fresh, but background re-focuses don't clobber a draft-in-progress.
  const draftQuery = useQuery<{ body: string }>({
    queryKey: ["/api/app/members", memberId, "draft"],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
  const aiDraftingAccessQuery = useQuery<{ allowed: boolean }>({
    queryKey: ["/api/app/ai-drafting/access"],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  // Fail CLOSED: AI Drafting is hidden by default, so only show when the server
  // has explicitly confirmed access. While the query is loading or if it errors
  // (data === undefined), the buttons stay hidden.
  const aiDraftingAllowed = aiDraftingAccessQuery.data?.allowed === true;
  const memberRoomNumber = memberDetailQuery.data?.roomNumber ?? null;
  const memberPropertyAddress =
    memberDetailQuery.data?.currentPropertyId != null
      ? (propertiesQuery.data ?? []).find((p) => p.id === memberDetailQuery.data?.currentPropertyId)?.address ?? null
      : null;
  const memberPropertyShortAddress = memberPropertyAddress ? deriveShortAddress(memberPropertyAddress) : null;
  // Stable fallback values for the timeline header: sourced from the member-detail
  // query so they survive a resolve-all when the "Unresolved messages only" filter
  // removes the member from the conversation list (allInboxRows lookup returns
  // nothing, causing the parent to pass memberName="" and memberPadsplitId=null).
  // The prop values are used first so live inbox-list updates take precedence; the
  // detail-query values kick in only once the props go empty/null.
  const stableHeaderName =
    memberName ||
    memberDetailQuery.data?.displayName ||
    memberDetailQuery.data?.name ||
    "";
  const stablePadsplitId = memberPadsplitId ?? memberDetailQuery.data?.padsplitMemberId ?? null;
  const stablePadsplitHref = getPadsplitProfileUrl({
    occupantId: memberDetailQuery.data?.occupantId,
  });
  const [draft, setDraft] = useState("");
  // Refs for draft persistence: hydration guard + touched guard + debounced autosave timer.
  // draftHydratedRef: true once initial server draft has been applied — prevents re-hydration on background refetch.
  // draftTouchedRef: true once the user has typed anything — prevents a slow initial GET from overwriting in-progress text.
  // clearGenRef: monotonically incremented on every send/schedule/Clear so an already-in-flight PUT can be detected
  //   and re-deleted if it lands after the DELETE.
  const draftHydratedRef = useRef(false);
  const draftTouchedRef = useRef(false);
  const clearGenRef = useRef(0);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Fast-poll window: after a send/schedule, poll every 2s for 30s so the
  // pending→delivered transition and any terminal failure surface quickly.
  const fastPollUntilRef = useRef<number>(0);
  // "Expand all threads" toggle — when true, every ChatBubble forces its
  // internal-thread panel open. Stored once at the timeline level so a single
  // header click controls the whole conversation.
  const [allThreadsOpen, setAllThreadsOpen] = useState(false);
  // Task #2995 — "Show unresolved only" filter. When true, inbound member
  // bubbles with resolved_at set are hidden so a host can focus on what still
  // needs attention. Admin/outbound bubbles always stay visible for context.
  // Per-session UI only — no schema change.
  const [showUnresolvedOnly, setShowUnresolvedOnly] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const draftTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const dragCounter = useRef(0);
  const rateLimit = useAttachmentRateLimit();

  // ── Co-presence awareness ──────────────────────────────────────────────────
  // Ephemeral, short-poll-based presence for shared-conversation awareness.
  // lastKeypressAtRef: tracks when the user last typed so we can toggle
  //   state between "typing" (< 3 s ago) and "viewing".
  // presencePeers: other teammates currently viewing/typing this conversation.
  // collisionWarningOpen: set when the Send button is clicked while a peer
  //   is actively typing — shows a non-blocking "send anyway?" dialog.
  const lastKeypressAtRef = useRef<number | null>(null);
  const [presencePeers, setPresencePeers] = useState<PresencePeer[]>([]);
  const [collisionWarningOpen, setCollisionWarningOpen] = useState(false);
  const [collisionWarningTypists, setCollisionWarningTypists] = useState<PresencePeer[]>([]);

  // Task #2120 — Tag each canned-response insertion (from the picker or the
  // inline `/`-trigger autocomplete) so usage is only recorded after the
  // containing message is actually sent.
  const [pendingCannedUses, setPendingCannedUses] = useState<PendingCannedUse[]>([]);

  function insertCannedResponse(text: string, cannedId?: number) {
    if (isImproving) return;
    const el = draftTextareaRef.current;
    let nextBody: string;
    if (!el) {
      nextBody = draft + (draft && !draft.endsWith("\n") ? "\n" : "") + text;
      setDraft(nextBody);
    } else {
      const start = el.selectionStart ?? draft.length;
      const end = el.selectionEnd ?? draft.length;
      const current = el.value;
      nextBody = current.slice(0, start) + text + current.slice(end);
      setDraft(nextBody);
      setTimeout(() => {
        el.focus();
        const newCursor = start + text.length;
        el.setSelectionRange(newCursor, newCursor);
      }, 0);
    }
    scheduleAutosave(nextBody);
    if (cannedId != null) {
      setPendingCannedUses((prev) => [...prev, { id: cannedId, insertedText: text }]);
    }
  }

  // ── Inline canned-response autocomplete ────────────────────────────────────
  // Typing the configurable trigger char (default `/`) at the start of the
  // textarea or directly after whitespace opens an inline menu showing
  // matching canned responses by title. Pressing Enter or clicking inserts
  // the response and replaces the trigger prefix; Escape dismisses. The
  // trigger is per-user, stored in localStorage, and editable on the Canned
  // Responses tab.
  const [cannedTrigger] = useCannedTrigger();
  const cannedListQuery = useQuery<CannedResponse[]>({
    queryKey: ["/api/app/canned-responses"],
    enabled: !!cannedTrigger,
  });
  const cannedResponses = cannedListQuery.data ?? [];
  const [triggerToken, setTriggerToken] = useState<
    { start: number; query: string } | null
  >(null);
  const [triggerHighlight, setTriggerHighlight] = useState(0);

  const triggerMatches = useMemo(() => {
    if (!triggerToken) return [];
    const q = String(triggerToken.query ?? "").toLowerCase();
    const matches = q
      ? cannedResponses.filter(
          (r) =>
            String(r.title ?? "").toLowerCase().includes(q) ||
            String(r.body ?? "").toLowerCase().includes(q),
        )
      : cannedResponses;
    return matches.slice(0, 8);
  }, [triggerToken, cannedResponses]);

  useEffect(() => {
    if (triggerHighlight >= triggerMatches.length) {
      setTriggerHighlight(0);
    }
  }, [triggerMatches.length, triggerHighlight]);

  function recomputeTriggerToken(text: string, cursor: number) {
    const next = detectCannedTriggerToken(text, cursor, cannedTrigger);
    setTriggerToken(next);
    if (next) setTriggerHighlight(0);
  }

  // ── Draft persistence mutations ─────────────────────────────────────────────
  // autosaveMutation silently upserts the current draft body; transient errors
  // are retried up to 2 times with default backoff so a single network hiccup
  // never silently drops a draft. Errors that survive all retries are swallowed
  // so typing is never interrupted.
  // vars.gen is the clearGenRef value at the time the PUT was fired; if a
  // send/schedule/Clear happened while the PUT was in-flight, we re-issue the
  // DELETE on onSuccess so the stale draft can't resurrect after the clear.
  const autosaveMutation = useMutation({
    mutationFn: ({ body }: { body: string; gen: number }) =>
      apiRequest("PUT", `/api/app/members/${memberId}/draft`, { body }),
    meta: { silentSave: true },
    retry: 2,
    onError: () => {},
    onSuccess: (_data, vars) => {
      if (clearGenRef.current !== vars.gen) {
        // A send/schedule/Clear fired while the PUT was in-flight — the draft
        // may have been restored on the server; delete it again idempotently.
        deleteDraftMutation.mutate();
      }
    },
  });

  // deleteDraftMutation is fire-and-forget: called on send / schedule / Clear.
  const deleteDraftMutation = useMutation({
    mutationFn: () =>
      apiRequest("DELETE", `/api/app/members/${memberId}/draft`),
    meta: { silentSave: true },
    onError: () => {},
  });

  // Hydrate the textarea with the server draft once per member session.
  // Guards:
  //   draftHydratedRef — prevents a background refetch from re-applying the
  //     server value after the user has already seen/edited it.
  //   draftTouchedRef — prevents a slow initial GET from overwriting text the
  //     user started typing before the response arrived. Once the user types
  //     anything, the local state is the source of truth.
  useEffect(() => {
    if (draftHydratedRef.current) return;
    if (draftQuery.data === undefined) return;
    if (draftTouchedRef.current) {
      // User typed before the initial fetch returned — their local state wins.
      draftHydratedRef.current = true;
      return;
    }
    draftHydratedRef.current = true;
    setDraft(draftQuery.data.body ?? "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQuery.data]);

  // Helper: schedule a debounced autosave for the given draft body (700 ms).
  // Captures the current clearGenRef value so that:
  //   a) If a send/schedule/Clear fires before the timer fires, the timer
  //      callback notices the generation mismatch and skips the PUT entirely.
  //   b) If the PUT is already in-flight when the clear fires, autosaveMutation
  //      onSuccess re-issues the DELETE.
  function scheduleAutosave(body: string) {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    const gen = clearGenRef.current;
    autosaveTimerRef.current = setTimeout(() => {
      if (clearGenRef.current !== gen) return; // cleared while debouncing — skip PUT
      autosaveMutation.mutate({ body, gen });
    }, 700);
  }

  // ── Presence heartbeat ─────────────────────────────────────────────────────
  // POST every 5 s while the conversation is open and visible. State toggles
  // between "typing" (last keypress < 3 s ago) and "viewing". On unmount
  // (or page-hide) DELETE the presence row so peers see an immediate exit.
  useEffect(() => {
    if (!canSend) return;
    let alive = true;
    const post = () => {
      if (!alive) return;
      // Never re-create a presence row while the tab is hidden.
      if (document.visibilityState === "hidden") return;
      const state = lastKeypressAtRef.current != null && Date.now() - lastKeypressAtRef.current < 3_000
        ? "typing"
        : "viewing";
      apiRequest("POST", `/api/app/members/${memberId}/presence`, { state }).catch(() => undefined);
    };
    post();
    const interval = setInterval(post, 5_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Immediately remove our row so peers stop seeing us as soon as we hide.
        apiRequest("DELETE", `/api/app/members/${memberId}/presence`).catch(() => undefined);
      } else {
        // Tab is visible again — announce presence right away instead of
        // waiting up to 5 s for the next interval tick.
        post();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      alive = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      apiRequest("DELETE", `/api/app/members/${memberId}/presence`).catch(() => undefined);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, canSend]);

  // ── Presence poll ──────────────────────────────────────────────────────────
  // GET others every 4 s. Stops when the component unmounts.
  useEffect(() => {
    if (!canSend) return;
    let alive = true;
    const poll = () => {
      if (!alive) return;
      apiRequest("GET", `/api/app/members/${memberId}/presence`)
        .then((r) => r.json())
        .then((data: { others?: PresencePeer[] }) => {
          if (!alive) return;
          setPresencePeers(data.others ?? []);
        })
        .catch(() => undefined);
    };
    poll();
    const interval = setInterval(poll, 4_000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, canSend]);

  // Auto-grow the composer textarea as the host types past the 5-line floor,
  // capping at a sensible max so it doesn't take over the screen. Runs on every
  // draft change so it also resets to the floor after a message is sent/cleared.
  useEffect(() => {
    const el = draftTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, 120), 280);
    el.style.height = `${next}px`;
  }, [draft]);

  function handleDraftChange(e: ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    draftTouchedRef.current = true;
    lastKeypressAtRef.current = Date.now();
    setDraft(value);
    // Clear undo stash once the user edits — they've accepted the improvement
    if (preImproveText !== null) setPreImproveText(null);
    const cursor = e.target.selectionStart ?? value.length;
    recomputeTriggerToken(value, cursor);
    scheduleAutosave(value);
  }

  function pickCannedFromTrigger(r: CannedResponse) {
    if (isImproving) return;
    if (!triggerToken) return;
    const el = draftTextareaRef.current;
    const current = el?.value ?? draft;
    const cursor = el?.selectionStart ?? current.length;
    const text = substituteCannedVars(r.body, {
      memberFirstName: memberName.split(" ")[0],
      memberFullName: memberName,
      propertyName,
      propertyFullAddress: memberPropertyAddress,
      roomNumber: memberRoomNumber,
      memberMoveInDate: memberDetailQuery.data?.moveInDate ?? null,
      memberLeaseEndDate: memberDetailQuery.data?.moveOutDate ?? null,
      weeklyRent: memberDetailQuery.data?.weeklyRate ?? null,
      hostName: senderName || null,
    });
    const next =
      current.slice(0, triggerToken.start) + text + current.slice(cursor);
    setDraft(next);
    scheduleAutosave(next);
    // Task #2120 — Inline `/`-trigger pick also counts as an insertion that
    // shouldn't be billed to the canned-response use counter unless the
    // message is actually sent. Tag it alongside picker-driven inserts.
    setPendingCannedUses((prev) => [...prev, { id: r.id, insertedText: text }]);
    setTriggerToken(null);
    setTimeout(() => {
      const node = draftTextareaRef.current;
      if (!node) return;
      node.focus();
      const pos = triggerToken.start + text.length;
      node.setSelectionRange(pos, pos);
    }, 0);
    // Task #2119 — Record the insert so usage insights (top responses,
    // by-team-member breakdown) reflect inline-autocomplete picks too,
    // not just clicks through the formal picker popover. Non-fatal.
    apiRequest("POST", `/api/app/canned-responses/${r.id}/record-use`)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses"] });
        queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/usage-summary"] });
      })
      .catch(() => { /* a missed usage tick should never block sending */ });
  }

  // Scroll management — gives us direct control over the chat message list's
  // scroll position without relying on Radix ScrollArea's internal viewport.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  // Tracks how many new messages have arrived while the user is scrolled up
  // reading history. Surfaces a "new messages ↓" badge so they can jump to
  // the latest without being yanked away mid-read.
  const [unseenCount, setUnseenCount] = useState(0);
  const lastSeenChatIdRef = useRef<number | null>(null);

  function scrollToBottom(behavior: ScrollBehavior = "instant") {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }

  // Returns true when the scroll container is within `threshold` px of the
  // bottom. Used to decide whether passive refetches should auto-scroll —
  // we only snap to bottom if the user was already near the end so we don't
  // interrupt them while they're reading older messages.
  function isNearBottom(threshold = 120) {
    const el = scrollContainerRef.current;
    if (!el) return true; // no container yet — default to "snap"
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  }

  // Task #1510 — Progressive timeline loading. We fetch the most recent
  // TIMELINE_PAGE_SIZE messages on open and lazily prepend older pages as
  // the user scrolls toward the top of the chat pane. The query key uses
  // an "infinite" suffix so it lives alongside the (legacy, full-payload)
  // timeline cache used by the recent-messages panels on the member detail
  // page; existing prefix-based invalidations still match because TanStack
  // Query v5 invalidates by prefix by default.
  type TimelinePage = { chats: TimelineChat[]; hasMore: boolean };
  const TIMELINE_QUERY_KEY = ["/api/app/members", memberId, "timeline", "infinite"] as const;
  const TIMELINE_PAGE_SIZE = 50;
  // When fetching an older page we capture the scroll metrics so we can
  // restore the user's exact viewport position after the prepended chats
  // grow the scroll container.
  const olderFetchScrollRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);

  const timelineQuery = useInfiniteQuery<
    TimelinePage,
    Error,
    InfiniteData<TimelinePage, string | null>,
    typeof TIMELINE_QUERY_KEY,
    string | null
  >({
    queryKey: TIMELINE_QUERY_KEY,
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ paginated: "1", limit: String(TIMELINE_PAGE_SIZE) });
      if (pageParam) params.set("before", pageParam);
      const res = await fetch(`/api/app/members/${memberId}/timeline?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      // Cursor for the next (older) page is the createdAt of the earliest
      // *real* chat in the latest page. Pending rows live only on the first
      // page and would create an invalid cursor here.
      const real = lastPage.chats.filter((c) => !c.pending);
      if (real.length === 0) return undefined;
      const earliest = real[0].createdAt;
      return earliest ? new Date(earliest).toISOString() : undefined;
    },
    // Poll so incoming messages appear without a manual refresh. Interval is
    // configurable via TIMELINE_POLL_INTERVAL_MS; pauses when tab is hidden.
    // After a send/schedule we fast-poll every 2s for 30s so the pending→delivered
    // transition and any terminal failure surface quickly.
    refetchInterval: (query) => {
      const now = Date.now();
      if (now < fastPollUntilRef.current) return 2_000;
      return TIMELINE_POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
    // staleTime 0 + refetchOnMount "always" defeats the global queryClient
    // staleTime:Infinity default so reopening a conversation always pulls the
    // latest timeline instead of a stale cached page; refetchOnWindowFocus
    // catches replies that arrived while the host was on another tab.
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: 0,
  });

  // Flatten pages into a single ascending list. The most-recent page is
  // pages[0]; older pages are appended via fetchNextPage in the order they
  // were fetched. To render oldest → newest we reverse before flattening.
  const allChats = useMemo<TimelineChat[]>(() => {
    const pages = timelineQuery.data?.pages ?? [];
    if (pages.length === 0) return [];
    const ordered: TimelineChat[] = [];
    for (let i = pages.length - 1; i >= 0; i--) {
      for (const c of pages[i].chats) ordered.push(c);
    }
    return ordered;
  }, [timelineQuery.data]);

  // ─── Schedule-for-later state ────────────────────────────────────────────
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleError, setScheduleError] = useState("");

  // ─── Messages Auto Draft — "Edit" hands a draft to this composer ─────────
  // Never bulldoze what the host is typing: if the reply box has text, ask
  // before replacing it. The bubble cancels the queued row only after a yes.
  const [autoDraftReplaceOpen, setAutoDraftReplaceOpen] = useState(false);
  const autoDraftReplaceResolveRef = useRef<((ok: boolean) => void) | null>(null);
  function settleAutoDraftReplace(ok: boolean) {
    autoDraftReplaceResolveRef.current?.(ok);
    autoDraftReplaceResolveRef.current = null;
    setAutoDraftReplaceOpen(false);
  }
  const autoDraftComposerApi = useMemo<AutoDraftComposerApi | null>(() => {
    if (!canSend) return null;
    return {
      confirmReplace: () => {
        const live = draftTextareaRef.current?.value ?? "";
        if (live.trim().length === 0) return Promise.resolve(true);
        return new Promise<boolean>((resolve) => {
          autoDraftReplaceResolveRef.current?.(false);
          autoDraftReplaceResolveRef.current = resolve;
          setAutoDraftReplaceOpen(true);
        });
      },
      load: (text: string) => {
        setDraft(text);
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
          autosaveMutation.mutate({ body: text, gen: clearGenRef.current });
        }, 600);
        requestAnimationFrame(() => draftTextareaRef.current?.focus());
      },
    };
    // autosaveMutation.mutate is stable across renders (react-query).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSend, memberId]);

  // ─── Suggest with AI (Composer RAG) ──────────────────────────────────────
  const [isSuggesting, setIsSuggesting] = useState(false);
  const suggestMutation = useMutation({
    // scopedMemberId/draftAtRequest are captured at CLICK time so the response
    // can be validated against where the host is NOW — mirroring the improve
    // mutation's stale-response guards. Suggest historically lacked them, so a
    // slow suggestion could land in another member's composer or bulldoze text
    // the host typed while it was thinking.
    mutationFn: ({ scopedMemberId }: { scopedMemberId: number; draftAtRequest: string }) =>
      apiRequest("POST", `/api/app/members/${scopedMemberId}/suggest`, {}).then(
        (r) => r.json() as Promise<{ draft: string; hint?: string }>,
      ),
    onSuccess: (data, variables) => {
      // Stale-response guard: host switched members while Suggest was in-flight —
      // never write this suggestion into the new thread's composer.
      if (variables.scopedMemberId !== memberId) return;
      // Typed-meanwhile guard: the composer stays editable while Suggest thinks.
      // If the host wrote anything since the request fired, THEIR text wins —
      // a suggestion must never overwrite a human draft.
      const liveDraft = draftTextareaRef.current?.value ?? "";
      if (liveDraft.trim().length > 0 && liveDraft !== variables.draftAtRequest) {
        toast({
          title: "Kept your draft",
          description: "You started typing while the AI was thinking, so your text was kept. Use Improve with AI to polish it.",
        });
        return;
      }
      if (data.draft && String(data.draft).trim()) {
        setDraft(data.draft);
        // Autosave the suggested draft
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
          autosaveMutation.mutate({ body: data.draft, gen: clearGenRef.current });
        }, 600);
      } else {
        toast({
          title: "No reply needed",
          description: data.hint ?? "There are no unanswered member messages to respond to.",
        });
      }
    },
    onError: (err, variables) => {
      // Member switched while the request was failing — swallow silently.
      if (variables.scopedMemberId !== memberId) return;
      toast({
        title: "Couldn't generate a suggestion",
        description: parseErrorMessage(err, "AI suggestion failed. Please try again."),
        variant: "destructive",
      });
    },
    onSettled: (_data, _err, variables) => {
      // Only clear the in-flight flag for the member active at click time
      // (the member-switch effect resets it for new members).
      if (variables.scopedMemberId !== memberId) return;
      setIsSuggesting(false);
    },
  });

  // ─── Improve with AI (Composer Phase 4) ───────────────────────────────────
  // preImproveText: the draft before improvement was applied — non-null while
  // an Undo is available. Cleared when the user types (accepting the edit) or
  // switches members.
  const [preImproveText, setPreImproveText] = useState<string | null>(null);
  const [isImproving, setIsImproving] = useState(false);
  const improveMutation = useMutation({
    mutationFn: ({ draft, scopedMemberId }: { draft: string; scopedMemberId: number }) =>
      apiRequest("POST", `/api/app/members/${scopedMemberId}/improve`, { draft }).then(
        (r) => r.json() as Promise<{ improved: string }>,
      ),
    onSuccess: (data, variables) => {
      // Stale-response guard: if the host switched members while Improve was in-flight,
      // silently discard the result so we never overwrite the new thread's draft.
      if (variables.scopedMemberId !== memberId) return;
      if (data.improved && String(data.improved).trim()) {
        setPreImproveText(variables.draft);
        setDraft(data.improved);
        // Autosave the improved draft
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
          autosaveMutation.mutate({ body: data.improved, gen: clearGenRef.current });
        }, 600);
      } else {
        // Empty result — leave draft untouched
        toast({
          title: "No improvement returned",
          description: "The AI couldn't improve this draft. Your original text is preserved.",
        });
      }
    },
    onError: (err, variables) => {
      // If the member switched while the request was failing, swallow silently —
      // the new thread's composer is unaffected.
      if (variables.scopedMemberId !== memberId) return;
      // Never destroys the draft — user sees a toast and their text is intact
      toast({
        title: "Couldn't improve draft",
        description: parseErrorMessage(err, "AI improvement failed. Your draft is unchanged."),
        variant: "destructive",
      });
    },
    onSettled: (_data, _err, variables) => {
      // Only clear the in-flight flag for the member that was active at click time.
      // (The member-switch effect already resets isImproving for new members.)
      if (variables.scopedMemberId !== memberId) return;
      setIsImproving(false);
    },
  });

  // ─── AI assist dispatch — decided at CLICK time from the LIVE composer ────
  // The Suggest/Improve buttons swap based on rendered `draft` state, which
  // can momentarily disagree with what's actually in the textarea (state
  // resets, hydration, re-renders between hover and click). The rule is the
  // content, not the label: if the composer has ANYTHING in it, that text is
  // the primary context and we IMPROVE it — never generate a from-scratch
  // suggestion over a human draft. Both buttons route through here, so even a
  // click landing on a stale-rendered button does the right thing.
  function handleAiAssist() {
    if (isSuggesting || isImproving) return;
    const liveDraft = draftTextareaRef.current?.value ?? draft;
    if (liveDraft.trim().length > 0) {
      setIsImproving(true);
      improveMutation.mutate({ draft: liveDraft, scopedMemberId: memberId });
    } else {
      setIsSuggesting(true);
      suggestMutation.mutate({ scopedMemberId: memberId, draftAtRequest: liveDraft });
    }
  }

  const { enabled: messagingOutage } = useMessagingOutage();

  // ─── Task #2987 — Post-reply "Mark as resolved?" prompt ──────────────────
  const [resolvePromptOpen, setResolvePromptOpen] = useState(false);
  const [resolvePromptCount, setResolvePromptCount] = useState(0);
  const { invalidateAfterResolveAll, invalidateAfterUnresolveAll } = useResolveInvalidations(memberId);
  // Task #3112 — re-opens exactly the messages a thread-level "Resolve all" (or
  // the post-reply resolve prompt) just resolved, so an accidental bulk-resolve
  // can be reversed inline from the success toast — matching the sidebar undo.
  const postSendUnresolveAllMutation = useMutation({
    mutationFn: (ids: number[]) =>
      apiRequest("POST", `/api/app/members/${memberId}/messages/unresolve`, { ids }).then((r) =>
        r.json(),
      ),
    onMutate: async (ids: number[]) => {
      // Task #4166 — undoing a bulk resolve re-opens `ids.length` messages, so
      // bump the conversation-list "N open" badge back up immediately (the
      // refetch backfills the oldest-open age hint).
      const { prevInbox } = await applyOptimisticInboxResolve(
        queryClient,
        memberId,
        incrementInboxRowUpdate(ids.length),
      );
      return { prevInbox };
    },
    onSuccess: () => {
      invalidateAfterUnresolveAll();
      toast({ title: "Messages re-opened" });
    },
    onError: (err, _ids, ctx) => {
      rollbackOptimisticInboxResolve(queryClient, ctx?.prevInbox);
      toast({ title: "Could not undo resolve", description: parseErrorMessage(err, "Couldn't undo resolve. Please try again."), variant: "destructive" });
    },
  });
  const resolveAllPostSendMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", `/api/app/members/${memberId}/messages/resolve-all`, {}).then(
        (r) => r.json() as Promise<{ count: number; ids: number[] }>,
      ),
    onMutate: async () => {
      // Optimistically remove this member from the unresolved queue.
      await queryClient.cancelQueries({ queryKey: UNRESOLVED_Q_KEY });
      const prev = queryClient.getQueryData<Array<{ memberId: number }>>(UNRESOLVED_Q_KEY);
      queryClient.setQueryData<Array<{ memberId: number }>>(UNRESOLVED_Q_KEY, (old) =>
        old ? old.filter((g) => g.memberId !== memberId) : old,
      );
      // Task #4166 — clear the conversation-list row's open count so its badge
      // doesn't lag this already-optimistic unresolved-queue removal.
      const { prevInbox } = await applyOptimisticInboxResolve(
        queryClient,
        memberId,
        resolveAllInboxRowUpdate,
      );
      return { prev, prevInbox };
    },
    onSuccess: (data, _vars, _ctx) => {
      setResolvePromptOpen(false);
      invalidateAfterResolveAll();
      const resolvedIds = data?.ids ?? [];
      toast({
        title: "Messages marked as resolved",
        action:
          resolvedIds.length > 0 ? (
            <ToastAction
              altText="Undo resolve all"
              onClick={() => postSendUnresolveAllMutation.mutate(resolvedIds)}
              data-testid="button-undo-post-send-resolve-all"
            >
              Undo
            </ToastAction>
          ) : undefined,
      });
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(UNRESOLVED_Q_KEY, ctx.prev);
      rollbackOptimisticInboxResolve(queryClient, ctx?.prevInbox);
      setResolvePromptOpen(false);
      toast({ title: "Could not resolve messages", description: parseErrorMessage(err, "Couldn't resolve messages. Please try again."), variant: "destructive" });
    },
  });

  // Mark inbound as read whenever the timeline changes. Inbound rows arrive with
  // senderType 'member' OR 'user' (both are member-side in production data), so
  // the filter must cover both — a 'member'-only filter left 'user' rows stuck
  // unread, keeping the sidebar total-unread badge permanently elevated.
  useEffect(() => {
    if (allChats.length === 0) return;
    const unreadIds = allChats
      .filter((c) => (c.senderType === "member" || c.senderType === "user") && c.isRead === false)
      .map((c) => c.id);
    if (unreadIds.length === 0) return;
    apiRequest("POST", `/api/app/members/${memberId}/read`, { ids: unreadIds })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
        // Do NOT invalidate UNREAD_COUNT_Q_KEY here: the sidebar "Messages"
        // badge now counts open (unresolved) messages, not unread ones, so
        // marking messages read must not lower the badge. Only resolve/
        // unresolve actions should refresh it (via invalidateResolveQueries).
      })
      .catch(() => undefined);
  }, [allChats, memberId]);

  // Tracks whether the deterministic first-load bottom-snap has completed for
  // the currently-selected member. Reset on member switch; consumed by the
  // first-data-arrival effect below so opening a long conversation always
  // lands at the most recent message instead of the top of the freshly-loaded
  // page.
  const initialScrollDoneRef = useRef(false);

  // Task #2296 — Match navigator state. Derives an ordered list of chat ids
  // whose body text contains every search word (case-insensitive). Tracks
  // which match is "current" (slightly stronger highlight, the one scrolled to).
  // `navigatorDismissed` lets the host close the bar without clearing the
  // inbox search; it resets on member switch or search-term change.
  const matchingChatIds = useMemo<number[]>(() => {
    const words = searchWords.filter((w) => w.length > 0);
    if (words.length === 0) return [];
    return allChats
      .filter((c) => {
        if (!c.messageText) return false;
        const lower = String(c.messageText).toLowerCase();
        if (!words.every((w) => lower.includes(w))) return false;
        // Respect the time-window filter so the match navigator only
        // navigates to messages within the selected window.
        if (searchSinceMs != null && new Date(c.createdAt).getTime() < searchSinceMs) return false;
        return true;
      })
      .map((c) => c.id);
  }, [allChats, searchWords, searchSinceMs]);

  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [navigatorDismissed, setNavigatorDismissed] = useState(false);

  // Task #2298 — Track the chat id of the "current" match so that when older
  // pages are auto-fetched and matchingChatIds grows at the front of the list,
  // we can re-locate the same bubble and update currentMatchIndex accordingly
  // (otherwise "1 of N" would silently start pointing at a different message).
  const currentMatchChatIdRef = useRef<number | null>(null);
  useEffect(() => {
    const id = matchingChatIds[currentMatchIndex];
    if (id != null) currentMatchChatIdRef.current = id;
  }, [currentMatchIndex, matchingChatIds]);
  useEffect(() => {
    const trackedId = currentMatchChatIdRef.current;
    if (trackedId == null) return;
    if (matchingChatIds[currentMatchIndex] === trackedId) return;
    const newIdx = matchingChatIds.indexOf(trackedId);
    if (newIdx >= 0) setCurrentMatchIndex(newIdx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingChatIds]);

  // Task #2298 — When a search is active, auto-fetch older pages so the match
  // navigator's count reflects the entire conversation, not just the most
  // recent page. Capped at SEARCH_AUTOFETCH_MAX_PAGES to bound work for very
  // long histories. Resets when the member or search term changes.
  const SEARCH_AUTOFETCH_MAX_PAGES = 10;
  const searchAutoFetchPagesRef = useRef(0);
  useEffect(() => {
    searchAutoFetchPagesRef.current = 0;
  }, [memberId, activeSearchTerm]);
  // Task #3402 — reset the "Show unresolved only" filter whenever the host
  // switches to a different conversation, so a filter enabled on one member
  // never leaves the next member's timeline looking empty.
  useEffect(() => {
    setShowUnresolvedOnly(false);
  }, [memberId]);
  // Task #3402 — keyboard shortcuts while the timeline is mounted:
  //   U → toggle "Show unresolved only"
  //   T → toggle "Expand all threads"
  // No-ops while focus is in a text input / textarea / contenteditable so the
  // host can type "u"/"t" in the draft box without triggering the toggles.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable
        ) {
          return;
        }
      }
      const key = e.key.toLowerCase();
      if (key === "u") {
        e.preventDefault();
        setShowUnresolvedOnly((v) => !v);
      } else if (key === "t") {
        e.preventDefault();
        setAllThreadsOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);
  useEffect(() => {
    if (searchWords.length === 0) return;
    if (navigatorDismissed) return;
    if (!timelineQuery.hasNextPage) return;
    if (timelineQuery.isLoading || timelineQuery.isFetchingNextPage) return;
    if (searchAutoFetchPagesRef.current >= SEARCH_AUTOFETCH_MAX_PAGES) return;
    // Let the deep-link backfill (Task #1510) walk pages first when a
    // focusChatId hasn't been resolved yet — otherwise both effects would
    // race on fetchNextPage().
    if (focusChatId != null && !allChats.some((c) => c.id === focusChatId)) return;
    // Preserve the visible viewport so prepended older content doesn't shove
    // the user's current chats down off-screen. Mirrors the scroll handler
    // path used by manual older-page fetches.
    const node = scrollContainerRef.current;
    if (node && !olderFetchScrollRef.current) {
      olderFetchScrollRef.current = {
        scrollHeight: node.scrollHeight,
        scrollTop: node.scrollTop,
      };
    }
    searchAutoFetchPagesRef.current += 1;
    void timelineQuery.fetchNextPage();
  }, [
    searchWords.length,
    navigatorDismissed,
    timelineQuery.hasNextPage,
    timelineQuery.isFetchingNextPage,
    timelineQuery.isLoading,
    timelineQuery.data,
    focusChatId,
    allChats,
    timelineQuery.fetchNextPage,
  ]);

  // Reset navigator when the member or search term changes.
  const prevSearchTermRef = useRef(activeSearchTerm);
  useEffect(() => {
    const termChanged = prevSearchTermRef.current !== activeSearchTerm;
    prevSearchTermRef.current = activeSearchTerm;
    setNavigatorDismissed(false);
    // Default current index to the position of focusChatId in the match list
    // so opening a content-matched member lands on the server-reported hit.
    if (focusChatId != null) {
      const idx = matchingChatIds.indexOf(focusChatId);
      setCurrentMatchIndex(idx >= 0 ? idx : 0);
    } else if (termChanged || matchingChatIds.length > 0) {
      setCurrentMatchIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, activeSearchTerm]);

  // When matchingChatIds first populates (timeline loads after member select),
  // snap the default current index to focusChatId if we haven't already.
  useEffect(() => {
    if (matchingChatIds.length === 0) return;
    if (focusChatId != null) {
      const idx = matchingChatIds.indexOf(focusChatId);
      if (idx >= 0) setCurrentMatchIndex(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingChatIds.length > 0 ? matchingChatIds[0] : null]);

  function scrollToMatchAt(idx: number) {
    const id = matchingChatIds[idx];
    if (id == null) return;
    // Defer one frame so the DOM has committed any re-render before we scroll.
    setTimeout(() => {
      const node = scrollContainerRef.current?.querySelector(
        `[data-testid="bubble-${id}"]`,
      ) as HTMLElement | null;
      if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  }

  function goToNextMatch() {
    if (matchingChatIds.length === 0) return;
    const next = (currentMatchIndex + 1) % matchingChatIds.length;
    setCurrentMatchIndex(next);
    scrollToMatchAt(next);
  }

  function goToPrevMatch() {
    if (matchingChatIds.length === 0) return;
    const prev = (currentMatchIndex - 1 + matchingChatIds.length) % matchingChatIds.length;
    setCurrentMatchIndex(prev);
    scrollToMatchAt(prev);
  }

  // Auto-scroll to the first/defaulted match when the navigator appears or
  // the match list refreshes (e.g. the timeline finished loading).
  const lastScrolledMatchRef = useRef<number | null>(null);
  useEffect(() => {
    if (navigatorDismissed) return;
    if (matchingChatIds.length === 0) return;
    const targetId = matchingChatIds[currentMatchIndex];
    if (targetId == null || lastScrolledMatchRef.current === targetId) return;
    lastScrolledMatchRef.current = targetId;
    scrollToMatchAt(currentMatchIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingChatIds, currentMatchIndex, navigatorDismissed]);

  // Reset the scroll tracker when member or search changes.
  useEffect(() => {
    lastScrolledMatchRef.current = null;
  }, [memberId, activeSearchTerm]);

  // Task #2298 — Also show the navigator while we're still auto-fetching older
  // pages, so the host sees a "Loading older messages…" indicator and an
  // updating count instead of nothing when the matches are buried deep in
  // history.
  const isSearchAutoFetching =
    searchWords.length > 0 &&
    timelineQuery.isFetchingNextPage &&
    searchAutoFetchPagesRef.current > 0;
  const showNavigator =
    searchWords.length > 0 &&
    !navigatorDismissed &&
    (matchingChatIds.length > 0 || isSearchAutoFetching);

  // Reset state when switching members
  useEffect(() => {
    setDraft("");
    draftHydratedRef.current = false;
    draftTouchedRef.current = false;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    setPendingCannedUses([]);
    setAttachments([]);
    setIsDragging(false);
    setScheduleOpen(false);
    setScheduleDate("");
    setScheduleTime("");
    setScheduleError("");
    setUnseenCount(0);
    lastSeenChatIdRef.current = null;
    dragCounter.current = 0;
    rateLimit.reset();
    initialScrollDoneRef.current = false;
    olderFetchScrollRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = "";
    setPreImproveText(null);
    setIsImproving(false);
    setIsSuggesting(false);
    // Reset TQ mutation state on member switch. Note: reset() does NOT cancel
    // the in-flight network request — stale response protection is handled by
    // the scopedMemberId guards in onSuccess/onError (both mutations).
    improveMutation.reset();
    suggestMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  // Auto-scroll behaviour after timeline data updates. Cases:
  //  1. We're loading an older page → skip; the next effect restores the
  //     user's exact viewport position after the prepended chats render.
  //  2. A deep-link focus target is active → skip; ChatBubble handles its
  //     own scrollIntoView.
  //  3. First page just arrived for this member → unconditionally snap to
  //     the bottom so opening a long conversation lands on the most recent
  //     message regardless of where the freshly-mounted scroll container
  //     happens to be. Replaces the older memberId-only auto-scroll effect
  //     which fired before chat bubbles had a chance to render.
  //  4. Otherwise (passive background refetch / poll) → if the user is near
  //     the bottom, snap them to the latest message; if they're scrolled up,
  //     increment the unseen-counter so the "new messages" badge appears.
  useEffect(() => {
    if (!timelineQuery.data) return;
    if (olderFetchScrollRef.current) return;
    if (focusChatId != null || focusThreadId != null) return;
    if (!initialScrollDoneRef.current) {
      initialScrollDoneRef.current = true;
      // Defer to the next frame so the freshly-rendered chat bubbles have
      // committed to the DOM and scrollHeight reflects the real content.
      const latestIdInitial =
        allChats.length > 0 ? allChats[allChats.length - 1].id : null;
      lastSeenChatIdRef.current = latestIdInitial;
      requestAnimationFrame(() => scrollToBottom("instant"));
      return;
    }
    const latestId = allChats.length > 0 ? allChats[allChats.length - 1].id : null;
    const prevSeen = lastSeenChatIdRef.current;
    if (isNearBottom()) {
      scrollToBottom("instant");
      lastSeenChatIdRef.current = latestId;
      if (unseenCount !== 0) setUnseenCount(0);
    } else if (latestId != null && prevSeen != null) {
      const newCount = allChats.filter((c) => c.id > prevSeen).length;
      if (newCount > 0) setUnseenCount((n) => Math.max(n, newCount));
    } else if (prevSeen == null) {
      // Baseline — first time we've seen any chats; don't show badge.
      lastSeenChatIdRef.current = latestId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineQuery.data]);

  // Task #1510 — Deep-link backfill. When the URL points at a chat
  // (?chat=) that's older than the most-recent page, iteratively fetch
  // the next (older) page until the chat appears or history is exhausted,
  // so the focus-and-highlight UX from notifications survives the move
  // to lazy pagination. For thread-only deep links (?thread= without a
  // resolvable ?chat=) we cap the lookback at THREAD_DEEPLINK_MAX_PAGES
  // additional pages so the inbox doesn't walk an entire long
  // conversation just because the parent chat id wasn't supplied.
  const THREAD_DEEPLINK_MAX_PAGES = 5;
  const threadBackfillPagesRef = useRef(0);
  // True only while a deep-link focus scroll is still settling. The focus
  // params stay in the URL for as long as the host reads that conversation,
  // so gating on them directly would disable scroll-up paging for the whole
  // visit. Instead this latches on when the focus target changes and clears
  // shortly after the scroll's own stability ceiling (3s in ChatBubble), so
  // the suppression only covers the scroll itself.
  const DEEPLINK_SETTLE_MS = 3500;
  const deepLinkSettlingRef = useRef(false);
  useEffect(() => {
    if (focusChatId == null && focusThreadId == null && focusPendingId == null) {
      deepLinkSettlingRef.current = false;
      return;
    }
    deepLinkSettlingRef.current = true;
    const t = setTimeout(() => { deepLinkSettlingRef.current = false; }, DEEPLINK_SETTLE_MS);
    return () => clearTimeout(t);
  }, [memberId, focusChatId, focusThreadId, focusPendingId]);
  useEffect(() => {
    threadBackfillPagesRef.current = 0;
  }, [memberId, focusThreadId, focusChatId]);
  useEffect(() => {
    if (focusChatId == null && focusThreadId == null) return;
    if (timelineQuery.isLoading || timelineQuery.isFetchingNextPage) return;
    if (!timelineQuery.hasNextPage) return;
    if (focusChatId != null) {
      if (allChats.some((c) => c.id === focusChatId)) return;
      void timelineQuery.fetchNextPage();
      return;
    }
    // focusThreadId-only path — bounded lookback.
    if (threadBackfillPagesRef.current >= THREAD_DEEPLINK_MAX_PAGES) return;
    threadBackfillPagesRef.current += 1;
    void timelineQuery.fetchNextPage();
  }, [
    focusChatId,
    focusThreadId,
    allChats,
    timelineQuery.hasNextPage,
    timelineQuery.isFetchingNextPage,
    timelineQuery.isLoading,
    timelineQuery.fetchNextPage,
  ]);

  // Task #1510 — When an older page has just been prepended, restore the
  // viewport so the user's content stays put. We compute the new scrollTop
  // as (newScrollHeight - oldScrollHeight + oldScrollTop). This effect must
  // run after the auto-scroll-to-bottom effect above so the ref is consumed
  // last; React runs effects in declaration order.
  useEffect(() => {
    if (!olderFetchScrollRef.current) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollHeight: oldH, scrollTop: oldT } = olderFetchScrollRef.current;
    el.scrollTop = el.scrollHeight - oldH + oldT;
    olderFetchScrollRef.current = null;
  }, [timelineQuery.data]);

  // Clear the unseen badge once the user scrolls back near the bottom.
  // Also drives Task #1510's older-page fetch when the user reaches the top
  // of the chat pane — capture scroll metrics first so the prepended content
  // doesn't yank the viewport.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    function onScroll() {
      const node = scrollContainerRef.current;
      if (!node) return;
      if (isNearBottom()) {
        if (unseenCount !== 0) setUnseenCount(0);
        if (allChats.length > 0) {
          lastSeenChatIdRef.current = allChats[allChats.length - 1].id;
        }
      }
      // While a deep link (?chat= / ?thread=) is still being scrolled to, the
      // timeline sits pinned near the top (the scroll-to-bottom effect is
      // suppressed for deep links). The focus scroll then passes through
      // scrollTop <= 80 on its way down, tripping this loader — and the
      // prepend restore below writes scrollTop directly, cancelling the
      // in-flight focus scroll and stranding the user above the target. The
      // dedicated deep-link backfill effect already fetches whatever older
      // pages the target needs, so skip the proximity loader for the ~3.5s
      // the focus scroll takes to settle. Deliberately a bounded window and
      // NOT `focusChatId != null`: the params persist in the URL for the
      // whole visit, so gating on them would leave the host unable to scroll
      // up for older messages until they opened another conversation.
      if (
        !deepLinkSettlingRef.current &&
        node.scrollTop <= 80 &&
        timelineQuery.hasNextPage &&
        !timelineQuery.isFetchingNextPage &&
        !olderFetchScrollRef.current
      ) {
        olderFetchScrollRef.current = { scrollHeight: node.scrollHeight, scrollTop: node.scrollTop };
        void timelineQuery.fetchNextPage();
      }
    }
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseenCount, timelineQuery.data, timelineQuery.hasNextPage, timelineQuery.isFetchingNextPage, allChats]);

  async function handleFiles(files: FileList | File[] | null) {
    if (!files) return;
    const list = Array.from(files);
    if (list.length === 0) return;
    const remaining = ATTACHMENT_MAX_COUNT - attachments.length;
    if (remaining <= 0) {
      toast({ title: "Attachment limit reached", description: `You can attach up to ${ATTACHMENT_MAX_COUNT} files.`, variant: "destructive" });
      return;
    }
    const incoming = list.slice(0, remaining);
    for (const file of incoming) {
      const id = makeAttachmentId();
      // Resolve the type BEFORE the allow-list check. The list already permits
      // image/heic, but browsers report an EMPTY file.type for .heic picks, so
      // has("") failed and the host was told "Only images and PDFs are
      // allowed" for a photo straight off their phone.
      const resolvedMime = uploadMimeType(file);
      if (!ALLOWED_ATTACHMENT_MIME.has(resolvedMime)) {
        setAttachments((prev) => [...prev, { id, name: file.name, mimeType: resolvedMime, size: file.size, status: "error", errorMessage: "Only images and PDFs are allowed" }]);
        continue;
      }
      if (file.size > ATTACHMENT_MAX_BYTES) {
        setAttachments((prev) => [...prev, { id, name: file.name, mimeType: resolvedMime, size: file.size, status: "error", errorMessage: `File is over ${Math.round(ATTACHMENT_MAX_BYTES / (1024 * 1024))}MB` }]);
        continue;
      }
      setAttachments((prev) => [...prev, { id, name: file.name, mimeType: resolvedMime, size: file.size, status: "uploading" }]);
      try {
        const urlRes = await apiRequest("POST", "/api/app/messages/upload-url", {});
        const { uploadUrl, objectPath } = (await urlRes.json()) as { uploadUrl: string; objectPath: string };
        const putRes = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": resolvedMime }, body: file });
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
        setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, status: "ready", objectPath } : a)));
      } catch (err: any) {
        setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, status: "error", errorMessage: parseErrorMessage(err, "Upload failed") } : a)));
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeAttachment(id: string) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  function handleDragEnter(e: React.DragEvent) {
    if (!canSend) return;
    if (!Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.preventDefault();
    dragCounter.current += 1;
    setIsDragging(true);
  }
  function handleDragOver(e: React.DragEvent) {
    if (!canSend) return;
    if (!Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function handleDragLeave(e: React.DragEvent) {
    if (!canSend) return;
    e.preventDefault();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragging(false);
  }
  function handleDrop(e: React.DragEvent) {
    if (!canSend) return;
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      void handleFiles(e.dataTransfer.files);
    }
  }

  const readyAttachments = attachments.filter((a) => a.status === "ready");
  const hasUploading = attachments.some((a) => a.status === "uploading");
  const hasErrors = attachments.some((a) => a.status === "error");
  const sendBlockedByRateLimit = rateLimit.isActive && readyAttachments.length > 0;

  const cleaningParams = new URLSearchParams(window.location.search);
  const cleaningProperty = cleaningParams.get("cleaningProperty");
  const isCleaningContext = !!cleaningProperty && (cleaningParams.get("memberId") ?? cleaningParams.get("member")) === String(memberId);
  const cleaningConfirmation = useCleaningConfirmation(cleaningProperty ?? "", () => {
    setDraft("");
    clearGenRef.current += 1;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    deleteDraftMutation.mutate();
    setPendingCannedUses([]);
    onSent();
    queryClient.invalidateQueries({ queryKey: TIMELINE_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
  });
  const requestSend = () => {
    if (!isCleaningContext) { sendMutation.mutate(); return; }
    if (attachments.length) {
      toast({ title: "Cleaning preview supports text only", description: "Remove attachments before previewing. Your draft has been kept.", variant: "destructive" });
      return;
    }
    void cleaningConfirmation.requestPreview({ action: "message", memberId, body: draft.trim() });
  };
  const sendMutation = useMutation({
    mutationFn: async () => {
      const attachmentUrls = readyAttachments
        .map((a) => annotateAttachmentUrl(a.objectPath ?? "", a.name))
        .filter((p) => !!p);
      const sentBody = draft.trim();
      const res = await apiRequest("POST", `/api/app/members/${memberId}/messages`, {
        messageText: sentBody,
        autoSend: true,
        attachmentUrls,
      });
      return { data: await res.json(), sentBody };
    },
    onMutate: async () => {
      // PadSplit send-UX — optimistic: insert a calm "Sent" bubble into the
      // timeline cache immediately, decoupled from the PadSplit push.
      await queryClient.cancelQueries({ queryKey: TIMELINE_QUERY_KEY });
      const optimisticId = -(Date.now()); // negative id so it can't clash with real ones
      const optimisticBubble: TimelineChat = {
        id: optimisticId,
        memberId,
        propertyId: null,
        senderType: "admin",
        senderId: null,
        senderName,
        senderAvatarUrl: null,
        messageText: draft.trim() || "",
        messageType: "text",
        isRead: true,
        deliveryStatus: "queued",
        source: "padpulse",
        createdAt: new Date(),
        updatedAt: new Date(),
        resolvedAt: null,
        resolvedBy: null,
        padsplitMessageId: null,
        externalMessageId: null,
        deliveryError: null,
        metadata: null,
        attachments: [],
        _optimistic: true,
        _optimisticStatus: "sent",
      };
      // Append to the newest page (pages[0]) so it shows at the bottom.
      queryClient.setQueryData<InfiniteData<TimelinePage, string | null>>(TIMELINE_QUERY_KEY, (old) => {
        if (!old || old.pages.length === 0) {
          return {
            pages: [{ chats: [optimisticBubble], hasMore: false }],
            pageParams: [null],
          };
        }
        const pages = old.pages.slice();
        pages[0] = { ...pages[0], chats: [...pages[0].chats, optimisticBubble] };
        return { ...old, pages };
      });
      // Scroll to bottom immediately so the optimistic "Sent" bubble is visible.
      requestAnimationFrame(() => scrollToBottom("smooth"));
      return { optimisticId };
    },
    onSuccess: async (result, _vars, context) => {
      // Task #2120 — Record canned-response usage only after the send
      // succeeded, and only for insertions whose text is still present in
      // the sent body.
      recordCannedUsesIfPresent(pendingCannedUses, result.sentBody);
      setPendingCannedUses([]);
      setDraft("");
      // Clear autosave timer and delete the persisted draft on successful send.
      // Increment clearGenRef first so any already-in-flight PUT's onSuccess
      // knows it must re-issue the DELETE rather than leave the draft alive.
      clearGenRef.current += 1;
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      deleteDraftMutation.mutate();
      setAttachments([]);
      rateLimit.reset();
      onSent();
      // Fast-poll for 30s so the pending→delivered transition surfaces quickly.
      fastPollUntilRef.current = Date.now() + 30_000;
      // Refetch the timeline first so the queued messages_pending row arrives,
      // then drop the optimistic placeholder. This avoids the "bubble flashes
      // and disappears" flicker that happened when we removed the optimistic
      // before the new data was in cache.
      await queryClient.refetchQueries({ queryKey: TIMELINE_QUERY_KEY });
      const optimisticId = context?.optimisticId;
      queryClient.setQueryData<InfiniteData<TimelinePage, string | null>>(TIMELINE_QUERY_KEY, (old) => {
        if (!old) return old;
        const pages = old.pages.map((p) => ({ ...p, chats: p.chats.filter((c) => c.id !== optimisticId) }));
        return { ...old, pages };
      });
      // Refresh the global pending count too (sidebar badge, Scheduled tab).
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
      // Task #2987 — After sending, check if the member has open inbound
      // messages and prompt to resolve them. Uses a member-scoped endpoint
      // (not capped) so the prompt fires even when the member isn't in the
      // top-8 dashboard list.
      try {
        const resp = await apiRequest("GET", `/api/app/members/${memberId}/messages/unresolved-count`);
        const { count } = await resp.json() as { count: number };
        if (count > 0) {
          setResolvePromptCount(count);
          setResolvePromptOpen(true);
        }
      } catch { /* network error — skip the prompt */ }
    },
    onError: async (err, _vars, context) => {
      // PadSplit send-UX — onError now only fires for a genuine REQUEST failure
      // (network, validation, outage 503, attachment rate-limit 429). The
      // PadSplit push itself no longer hard-errors on the hot path — it's
      // fire-and-forget with terminal-failure routing via the bell + Team
      // Inbox — so there is no PADSPLIT_SEND_FAILED dialog on the send path.
      // We surface the request error, mark the optimistic bubble failed briefly,
      // then refetch so the persisted pending row (if the enqueue landed) owns
      // the state, and drop the placeholder.
      const optimisticId = context?.optimisticId;
      queryClient.setQueryData<InfiniteData<TimelinePage, string | null>>(TIMELINE_QUERY_KEY, (old) => {
        if (!old) return old;
        const pages = old.pages.map((p) => ({
          ...p,
          chats: p.chats.map((c) => (c.id === optimisticId ? { ...c, _optimisticStatus: "failed" as const } : c)),
        }));
        return { ...old, pages };
      });
      const armed = rateLimit.armFromError(err);
      if (armed) {
        toast({ title: "Attachment limit reached", description: armed.message, variant: "destructive" });
      } else {
        toast({ title: "Couldn't send message", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
      }
      try {
        await queryClient.refetchQueries({ queryKey: TIMELINE_QUERY_KEY });
        await queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
        await queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
      } finally {
        // Drop the optimistic bubble — the persisted failed pending row now
        // owns the failed state + retry control. If the refetch failed for
        // any reason we still drop it so we don't leave a dead retry button.
        queryClient.setQueryData<InfiniteData<TimelinePage, string | null>>(TIMELINE_QUERY_KEY, (old) => {
          if (!old) return old;
          const pages = old.pages.map((p) => ({ ...p, chats: p.chats.filter((c) => c.id !== optimisticId) }));
          return { ...old, pages };
        });
      }
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: async ({ sendAt }: { sendAt: string }) => {
      if (isCleaningContext) throw new Error("Cleaning-context messages require Preview message and Send now. Scheduled manual messages are not supported here; your draft is unchanged.");
      const attachmentUrls = readyAttachments
        .map((a) => annotateAttachmentUrl(a.objectPath ?? "", a.name))
        .filter((p) => !!p);
      const sentBody = draft.trim();
      const res = await apiRequest("POST", `/api/app/members/${memberId}/messages`, {
        messageText: sentBody,
        autoSend: true,
        sendAt,
        attachmentUrls,
      });
      return { data: await res.json(), sentBody };
    },
    onSuccess: (result, { sendAt }) => {
      // Task #2120 — A scheduled message has been accepted and queued for
      // delivery. Count its canned-response usage now; the worker will own
      // actual transport.
      recordCannedUsesIfPresent(pendingCannedUses, result.sentBody);
      setPendingCannedUses([]);
      setDraft("");
      // Clear autosave timer and delete the persisted draft on successful schedule.
      // Increment clearGenRef first so any already-in-flight PUT re-deletes.
      clearGenRef.current += 1;
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      deleteDraftMutation.mutate();
      setAttachments([]);
      setScheduleOpen(false);
      setScheduleDate("");
      setScheduleTime("");
      setScheduleError("");
      rateLimit.reset();
      onSent();
      // Fast-poll for 30s so the scheduled→sent→delivered transition surfaces quickly.
      fastPollUntilRef.current = Date.now() + 30_000;
      // Refresh both the timeline (so the scheduled bubble appears inline)
      // and the global pending count (Awaiting/Scheduled tabs + sidebar).
      queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
      toast({ title: `Scheduled for ${new Date(sendAt).toLocaleString()}` });
    },
    onError: (err) => {
      const armed = rateLimit.armFromError(err);
      if (armed) {
        toast({ title: "Attachment limit reached", description: armed.message, variant: "destructive" });
        return;
      }
      toast({ title: "Couldn't schedule message", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
    },
  });

  function handleScheduleConfirm() {
    setScheduleError("");
    if (!scheduleDate) { setScheduleError("Pick a date."); return; }
    const iso = `${scheduleDate}T${scheduleTime || "00:00"}`;
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) { setScheduleError("Invalid date/time."); return; }
    if (dt <= new Date()) { setScheduleError("Schedule time must be in the future."); return; }
    scheduleMutation.mutate({ sendAt: dt.toISOString() });
  }

  const sendDisabled =
    !canSend ||
    cleaningConfirmation.busy ||
    sendMutation.isPending ||
    scheduleMutation.isPending ||
    hasUploading ||
    hasErrors ||
    sendBlockedByRateLimit ||
    isSuggesting ||
    isImproving ||
    (draft.trim().length === 0 && readyAttachments.length === 0);

  // Number of visible chat bubbles that have at least one thread reply.
  // Drives the count badge on the "Expand all threads" button.
  const threadedChatCount = useMemo(
    () => allChats.reduce((n, c) => n + ((c.replyCount ?? 0) > 0 ? 1 : 0), 0),
    [allChats],
  );

  // Timestamp (ms) of the OLDEST unresolved inbound member message. Resolution
  // applies only to inbound member messages, so outbound host/internal (admin)
  // messages never count as "unresolved" on their own (Task #3635). Null when
  // there are no unresolved inbound messages at all.
  const oldestUnresolvedAt = useMemo(() => {
    let oldest: number | null = null;
    for (const c of allChats) {
      if (c.senderType !== "admin" && c.resolvedAt == null) {
        const t = new Date(c.createdAt).getTime();
        if (oldest == null || t < oldest) oldest = t;
      }
    }
    return oldest;
  }, [allChats]);

  // Task #2995 / #3635 — when "Show unresolved only" is on, show the oldest
  // unresolved inbound message AND everything newer (from anybody, so host
  // replies that follow stay in context); hide the older resolved history.
  // With no unresolved inbound message at all, hide everything (empty-state).
  const visibleChats = useMemo(() => {
    if (!showUnresolvedOnly) return allChats;
    if (oldestUnresolvedAt == null) return [];
    return allChats.filter((c) => new Date(c.createdAt).getTime() >= oldestUnresolvedAt);
  }, [allChats, showUnresolvedOnly, oldestUnresolvedAt]);

  // Count of unresolved inbound bubbles — drives the "(N)" suffix on the toggle
  // label and the always-visible "Resolve all" control in the header.
  const unresolvedInboundCount = useMemo(
    () => allChats.filter((c) => c.senderType !== "admin" && c.resolvedAt == null).length,
    [allChats],
  );

  // Tasks already represented on a loaded chat bubble (the green "Ticket #N"
  // pill, driven by member_chat_workflow.maintenance_task_id). We don't also
  // surface those as standalone reported-task cards — the bubble pill already
  // tells that story. Only de-dupe against loaded bubbles; an unloaded older
  // page can't have a pill on screen yet anyway.
  const linkedTaskIds = useMemo(() => {
    const ids = new Set<number>();
    for (const c of allChats) {
      const tid = c.workflow?.maintenanceTaskId;
      if (tid != null) ids.add(tid);
    }
    return ids;
  }, [allChats]);

  // Reported-task cards to interleave. Gated to the currently-loaded chat
  // window: a card whose createdAt is older than the oldest *loaded* message is
  // held back until that page scrolls into view, so cards appear chronologically
  // alongside the messages around them (rather than all stacking at the top).
  // The cutoff keys off `allChats` (everything fetched), NOT `visibleChats`:
  // display filters like "show unresolved only" must not move the pagination
  // window, or they'd wrongly hide older cards (or, when the filter empties the
  // visible list while chats are loaded, surface every card at the top).
  // When the conversation has no chat messages at all, show every reported task.
  const visibleReportedTasks = useMemo(() => {
    const all = reportedTasksQuery.data ?? [];
    if (all.length === 0) return [];
    const deduped = all.filter((t) => !linkedTaskIds.has(t.id));
    if (allChats.length === 0) return deduped;
    const oldest = new Date(allChats[0].createdAt).getTime();
    return deduped.filter((t) => new Date(t.createdAt).getTime() >= oldest);
  }, [reportedTasksQuery.data, linkedTaskIds, allChats]);

  // Cleaning proof cards, gated to the loaded chat window like reported tasks.
  const visibleCleaningProofs = useMemo(() => {
    // Array.isArray guards against stubbed/error payloads shaped as objects.
    const all = Array.isArray(cleaningProofsQuery.data) ? cleaningProofsQuery.data : [];
    if (all.length === 0) return [];
    if (allChats.length === 0) return all;
    const oldest = new Date(allChats[0].createdAt).getTime();
    return all.filter((c) => new Date(c.createdAt).getTime() >= oldest);
  }, [cleaningProofsQuery.data, allChats]);

  // Merged, time-sorted render list (chat bubbles + reported-task cards),
  // grouped by date. Built solely for rendering — `allChats`/`visibleChats`
  // stay untouched so every effect keyed on them (scroll, unread, search,
  // deep-link) keeps working on the chat stream alone.
  type TimelineItem =
    | { kind: "chat"; key: string; at: number; chat: TimelineChat }
    | { kind: "task"; key: string; at: number; task: ReportedTaskSummary }
    | { kind: "cleaning"; key: string; at: number; card: CleaningSubmissionSummary };
  const timelineByDate = useMemo(() => {
    const items: TimelineItem[] = [];
    for (const chat of visibleChats) {
      items.push({ kind: "chat", key: `chat-${chat.id}`, at: new Date(chat.createdAt).getTime(), chat });
    }
    for (const task of visibleReportedTasks) {
      items.push({ kind: "task", key: `task-${task.id}`, at: new Date(task.createdAt).getTime(), task });
    }
    for (const card of visibleCleaningProofs) {
      items.push({ kind: "cleaning", key: `cleaning-${card.submissionId}`, at: new Date(card.createdAt).getTime(), card });
    }
    // Ascending by time; ties keep chats before task cards for a stable order.
    items.sort((a, b) => (a.at - b.at) || (a.kind === b.kind ? 0 : a.kind === "chat" ? -1 : 1));
    const groups: { label: string; items: TimelineItem[] }[] = [];
    let lastDate = "";
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    for (const item of items) {
      const d = new Date(item.at);
      let label: string;
      if (d.toDateString() === now.toDateString()) label = "Today";
      else if (d.toDateString() === yesterday.toDateString()) label = "Yesterday";
      else label = d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
      if (label !== lastDate) {
        groups.push({ label, items: [item] });
        lastDate = label;
      } else {
        groups[groups.length - 1].items.push(item);
      }
    }
    return groups;
  }, [visibleChats, visibleReportedTasks, visibleCleaningProofs]);

  return (
    <AutoDraftComposerContext.Provider value={autoDraftComposerApi}>
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <div className="border-b border-gray-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-2">
          {onBack && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 w-8 shrink-0 p-0 text-gray-600 sm:hidden"
              onClick={onBack}
              data-testid="button-back-to-inbox-list"
              title="Back to conversations"
              aria-label="Back to conversations"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          {!inboxListOpen && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="hidden h-8 w-8 shrink-0 p-0 text-gray-500 sm:inline-flex"
              onClick={onToggleInboxList}
              data-testid="button-expand-inbox-list"
              title="Show member list"
              ref={toggleInboxListButtonRef}
              aria-controls="inbox-sidebar"
              aria-expanded={false}
            >
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
          )}
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${avatarColor(stableHeaderName)}`}>
            {initials(stableHeaderName || "?")}
          </div>
          <div className="flex items-center gap-1.5">
            <div className="text-sm font-semibold text-gray-900" data-testid="text-active-member">
              {stableHeaderName || `Member #${memberId}`}
            </div>
            <MemberSourceBadge padsplitMemberId={stablePadsplitId} memberId={memberId} href={stablePadsplitHref} />
            {stablePadsplitId && !stablePadsplitHref && (
              <span className="text-xs text-gray-500" data-testid="text-padsplit-profile-unavailable">Profile unavailable</span>
            )}
            {(() => {
              const rawStatus = memberDetailQuery.data?.status;
              const bucket = bucketMemberStatus(rawStatus);
              if (!rawStatus) return null;
              const statusClasses =
                bucket === "terminated"
                  ? "bg-red-100 text-red-700"
                  : bucket === "active"
                    ? "bg-green-100 text-green-700"
                    : bucket === "booking_request"
                      ? "bg-yellow-400 text-yellow-900 font-semibold"
                      : "bg-gray-100 text-gray-600";
              const label =
                bucket === "booking_request" ? "Request" : rawStatus;
              return (
                <span
                  className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${statusClasses}`}
                  data-testid={`badge-member-status-${memberId}`}
                >
                  {label}
                </span>
              );
            })()}
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={`ml-auto h-8 px-2 text-xs ${showUnresolvedOnly ? "text-brand" : "text-gray-600"}`}
            onClick={() => setShowUnresolvedOnly((v) => !v)}
            data-testid="button-toggle-unresolved-only"
            title={`${showUnresolvedOnly ? "Show all messages" : "Show only unresolved messages"} (shortcut: U)`}
            aria-pressed={showUnresolvedOnly}
          >
            <Filter className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline" data-testid="text-toggle-unresolved-only-label">
              {showUnresolvedOnly ? "Show all" : "Unresolved Only"}
              {!showUnresolvedOnly && unresolvedInboundCount > 0 ? ` (${unresolvedInboundCount})` : ""}
            </span>
            <kbd className="ml-1 hidden rounded border border-gray-300 bg-gray-100 px-1 text-[10px] font-medium text-gray-500 sm:inline">
              U
            </kbd>
          </Button>
          {/* Quo messaging — hand a call to the Quo app (renders only when Quo is the provider). */}
          <QuoCallButton memberId={memberId} memberName={memberName} />
          {/* Resolve all — visible whenever there's at least 1 unresolved inbound message */}
          {unresolvedInboundCount >= 1 && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 px-2 text-xs text-green-700 hover:text-green-800 hover:bg-green-50"
              onClick={() => resolveAllPostSendMutation.mutate()}
              disabled={resolveAllPostSendMutation.isPending}
              data-testid="button-resolve-all-thread"
              title={`Resolve all ${unresolvedInboundCount} unresolved messages`}
            >
              <CheckCircle className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Resolve all ({unresolvedInboundCount})</span>
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-xs text-gray-600"
            onClick={() => setAllThreadsOpen((v) => !v)}
            data-testid="button-toggle-all-threads"
            title={`${allThreadsOpen ? "Collapse all internal threads" : "Expand all internal threads"} (shortcut: T)`}
            aria-pressed={allThreadsOpen}
          >
            {allThreadsOpen ? (
              <ChevronUp className="h-4 w-4 sm:mr-1" />
            ) : (
              <ChevronDown className="h-4 w-4 sm:mr-1" />
            )}
            <span className="hidden sm:inline" data-testid="text-toggle-all-threads-label">
              {allThreadsOpen ? "Collapse threads" : "Expand Threads"}
              {threadedChatCount > 0 ? ` (${threadedChatCount})` : ""}
              <kbd className="ml-1 text-[10px] text-gray-400">(T)</kbd>
            </span>
            <kbd className="ml-1 hidden rounded border border-gray-300 bg-gray-100 px-1 text-[10px] font-medium text-gray-500 sm:inline">
              T
            </kbd>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-xs text-gray-600"
            onClick={onToggleSidebar}
            data-testid="button-toggle-member-sidebar"
            data-tour="messages-member-info"
            title={sidebarOpen ? "Hide member info" : "Show member info"}
            ref={toggleSidebarButtonRef}
            aria-expanded={sidebarOpen}
            aria-controls="member-info-sidebar"
          >
            {sidebarOpen ? (
              <PanelRightClose className="h-4 w-4 sm:mr-1" />
            ) : (
              <PanelRightOpen className="h-4 w-4 sm:mr-1" />
            )}
            <span className="hidden sm:inline">Member info</span>
          </Button>
        </div>
      </div>
      {/* Merged-stay notice (transfer plan) — a merged stay's chat rows live
          on the canonical stay, so instead of an empty thread show where the
          conversation continues. The inbox list already hides merged rows;
          this covers deep links (?member=N) to the old stay. */}
      {memberDetailQuery.data?.mergedIntoMemberId != null && (
        <div
          className="flex items-center gap-2 border-b border-blue-200 bg-blue-50 px-4 py-2 text-xs text-blue-900"
          data-testid="banner-timeline-member-transferred"
        >
          <AlertCircle className="h-4 w-4 shrink-0 text-blue-700" />
          <span>
            This member transferred — the conversation continues on the current stay.
          </span>
          <Link
            href={`/app/messages?member=${memberDetailQuery.data.mergedIntoMemberId}`}
            className="ml-auto shrink-0 font-medium text-blue-700 underline hover:text-blue-800"
            data-testid="link-timeline-transferred-canonical"
          >
            Open current conversation
          </Link>
        </div>
      )}
      {/* Task #2296 — Match navigator bar */}
      {showNavigator && (
        <div
          className="flex items-center gap-1.5 bg-yellow-50 border-b border-yellow-200 px-3 py-1.5 text-xs text-yellow-900"
          data-testid="match-navigator"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || (e.key === "Enter" && !e.shiftKey)) {
              e.preventDefault();
              goToNextMatch();
            } else if (e.key === "ArrowUp" || (e.key === "Enter" && e.shiftKey)) {
              e.preventDefault();
              goToPrevMatch();
            }
          }}
          tabIndex={0}
        >
          <span className="font-medium" data-testid="match-navigator-count">
            {matchingChatIds.length === 0
              ? "0 matches"
              : `${currentMatchIndex + 1} of ${matchingChatIds.length}`}
          </span>
          <span className="text-yellow-700">
            for &ldquo;<span className="font-medium">{activeSearchTerm}</span>&rdquo;
          </span>
          {isSearchAutoFetching && (
            <span
              className="flex items-center gap-1 text-yellow-700"
              data-testid="status-match-navigator-loading"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Loading older messages…</span>
            </span>
          )}
          <button
            type="button"
            onClick={goToPrevMatch}
            className="ml-1 rounded p-0.5 hover:bg-yellow-200 disabled:opacity-40"
            disabled={matchingChatIds.length <= 1}
            title="Previous match (Shift+Enter / ↑)"
            data-testid="button-match-prev"
            aria-label="Previous match"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={goToNextMatch}
            className="rounded p-0.5 hover:bg-yellow-200 disabled:opacity-40"
            disabled={matchingChatIds.length <= 1}
            title="Next match (Enter / ↓)"
            data-testid="button-match-next"
            aria-label="Next match"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setNavigatorDismissed(true)}
            className="ml-auto rounded p-0.5 hover:bg-yellow-200"
            title="Close match navigator"
            data-testid="button-match-close"
            aria-label="Close match navigator"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="relative flex-1 min-h-0">
      <div
        ref={scrollContainerRef}
        className="absolute inset-0 overflow-y-auto"
      >
        <div className="space-y-1 p-4">
          {timelineQuery.isFetchingNextPage && (
            <div
              className="flex items-center justify-center py-2 text-xs text-gray-400"
              data-testid="status-loading-older-messages"
            >
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Loading older messages…
            </div>
          )}
          {timelineQuery.isLoading ? (
            <div className="flex h-32 items-center justify-center text-gray-400">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : allChats.length === 0 && visibleReportedTasks.length === 0 ? (
            // Task #3417 — the conversation genuinely has zero messages (e.g. a
            // freshly-created member with no chat history). Show a clear empty-
            // state so the pane feels intentional instead of looking broken.
            // Distinct from the "Show unresolved only" empty-state below, which
            // only fires when the filter has hidden otherwise-present messages.
            // A member with no chats but reported tasks falls through to the
            // merged render below so their task cards still show.
            <div
              className="flex h-full flex-col items-center justify-center gap-3 py-12 text-center"
              data-testid="empty-no-messages"
            >
              <Send className="h-8 w-8 text-gray-300" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">
                  No messages yet
                </p>
                <p className="text-xs text-gray-500">
                  Start the conversation below.
                </p>
              </div>
              {canSend && (
                <Button
                  variant="default"
                  size="sm"
                  data-testid="button-start-conversation"
                  onClick={() => draftTextareaRef.current?.focus()}
                >
                  Start the conversation
                </Button>
              )}
            </div>
          ) : showUnresolvedOnly && visibleChats.length === 0 && allChats.length > 0 ? (
            // Task #3416 / #3635 — the "Show unresolved only" filter is on but
            // no unresolved inbound member message remains (the conversation is
            // all resolved inbound and/or host replies). Explain why the pane
            // looks blank and how to bring the messages back instead of showing
            // an empty void.
            <div
              className="flex h-full flex-col items-center justify-center gap-3 py-12 text-center"
              data-testid="empty-unresolved-filter"
            >
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">
                  All caught up — no unresolved messages.
                </p>
                <p className="text-xs text-gray-500">
                  No member messages are waiting on a reply. Toggle the filter off to see the full conversation.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-xs"
                onClick={() => setShowUnresolvedOnly(false)}
                data-testid="button-show-all-from-empty"
              >
                <Filter className="mr-1 h-3.5 w-3.5" /> Show all messages
              </Button>
              {canSend && (
                <Button
                  variant="default"
                  size="sm"
                  data-testid="button-start-conversation-unresolved"
                  onClick={() => draftTextareaRef.current?.focus()}
                >
                  Start the conversation
                </Button>
              )}
            </div>
          ) : (
            timelineByDate.map((group) => (
              <div key={group.label}>
                <div className="my-3 flex items-center gap-2 text-xs text-gray-400">
                  <div className="flex-1 border-t border-gray-200" />
                  <span>{group.label}</span>
                  <div className="flex-1 border-t border-gray-200" />
                </div>
                {group.items.map((item) =>
                  item.kind === "task" ? (
                    <ReportedTaskCard
                      key={item.key}
                      task={item.task}
                      memberName={memberName}
                      onReply={canSend ? () => draftTextareaRef.current?.focus() : undefined}
                    />
                  ) : item.kind === "cleaning" ? (
                    <CleaningProofCard key={item.key} card={item.card} memberName={memberName} />
                  ) : (
                    <ChatBubble
                      key={item.key}
                      chat={item.chat}
                      memberId={memberId}
                      forceThreadsOpen={allThreadsOpen}
                      isFocused={
                        (focusChatId != null && item.chat.id === focusChatId) ||
                        (focusPendingId != null && item.chat.pending?.id === focusPendingId)
                      }
                      focusThreadId={focusThreadId}
                      teamMembers={teamMembers}
                      isSearchMatch={!navigatorDismissed && searchWords.length > 0 && matchingChatIds.includes(item.chat.id)}
                      isCurrentMatch={!navigatorDismissed && searchWords.length > 0 && matchingChatIds[currentMatchIndex] === item.chat.id}
                      searchWords={!navigatorDismissed ? searchWords : []}
                    />
                  ),
                )}
              </div>
            ))
          )}
        </div>
      </div>
        {unseenCount > 0 && (
          <button
            type="button"
            onClick={() => {
              scrollToBottom("smooth");
              setUnseenCount(0);
              if (allChats.length > 0) {
                lastSeenChatIdRef.current = allChats[allChats.length - 1].id;
              }
            }}
            className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-lg hover:bg-blue-700"
            data-testid="button-new-messages-jump"
          >
            <span data-testid="text-new-messages-count">
              {unseenCount} more recent {unseenCount === 1 ? "message" : "messages"}
            </span>
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div
        className="relative border-t border-gray-200 bg-white p-3"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="composer-reply"
        data-tour="messages-compose"
      >
        {memberDetailQuery.data?.mergedIntoMemberId != null ? (
          /* Merged-stay composer lock: sends to a merged stay are silently
             redirected to the canonical member server-side, but this stay's
             timeline would never show the pending bubble — the host would
             assume failure and resend, duplicating the message. Replace the
             composer with a pointer to the canonical conversation. */
          <div
            className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900"
            data-testid="banner-composer-member-transferred"
          >
            <AlertCircle className="h-4 w-4 shrink-0 text-blue-700" />
            <span>
              This member transferred — send messages from the{" "}
              <Link
                href={`/app/messages?member=${memberDetailQuery.data.mergedIntoMemberId}`}
                className="font-medium text-blue-700 underline hover:text-blue-800"
                data-testid="link-composer-transferred-canonical"
              >
                current conversation
              </Link>
              .
            </span>
          </div>
        ) : messagingOutage ? (
          <MessagingOutageBanner />
        ) : (
        <>
        {isDragging && (
          <div
            className="pointer-events-none absolute inset-1 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-blue-400 bg-blue-50/90 text-sm font-medium text-blue-700"
            data-testid="overlay-drop-attachments"
          >
            Drop files to attach
          </div>
        )}
        {rateLimit.isActive && (
          <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="banner-reply-rate-limit">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <div className="font-medium" data-testid="text-reply-rate-limit-message">{rateLimit.message || "Attachment limit reached."}</div>
              <div className="mt-0.5 text-xs">Try again in <span className="font-semibold tabular-nums" data-testid="text-reply-rate-limit-countdown">{formatRateLimitCountdown(rateLimit.remaining)}</span>. Your message and attachments are still here.</div>
            </div>
          </div>
        )}
        {attachments.length > 0 && (
          <ul className="mb-2 space-y-1.5" data-testid="list-reply-attachments">
            {attachments.map((a) => {
              const isImage = a.mimeType.startsWith("image/");
              return (
                <li key={a.id} className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm ${a.status === "error" ? "border-red-200 bg-red-50" : "border-gray-200 bg-gray-50"}`} data-testid={`reply-attachment-item-${a.id}`}>
                  {isImage ? <ImageIcon className="h-4 w-4 shrink-0 text-gray-500" /> : <FileText className="h-4 w-4 shrink-0 text-gray-500" />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium" data-testid={`text-reply-attachment-name-${a.id}`}>{a.name}</div>
                    <div className="truncate text-xs text-gray-500">
                      {formatBytes(a.size)}
                      {a.status === "uploading" && " · Uploading..."}
                      {a.status === "ready" && " · Ready"}
                      {a.status === "error" && a.errorMessage ? ` · ${a.errorMessage}` : ""}
                    </div>
                  </div>
                  {a.status === "uploading" ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />
                  ) : (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeAttachment(a.id)} className="h-7 w-7 p-0" data-testid={`button-remove-reply-attachment-${a.id}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {hasErrors && (
          <p className="mb-2 text-xs text-red-500" data-testid="text-reply-attachment-error-summary">
            Remove failed attachments before sending.
          </p>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="input-reply-attachment-file"
        />
        {presencePeers.length > 0 && (
          <div className="mb-1.5" data-testid="presence-indicator-wrapper">
            <PresenceIndicator peers={presencePeers} />
          </div>
        )}
        {/* Composer toolbar.
            Mobile (default): the textarea takes its own full-width line, the
            attachment / canned / schedule controls sit in a left utility bar,
            and the AI / undo / clear / send actions sit in a bottom action row
            (utility bar left-aligned, actions pushed right on the same wrapped
            line). Desktop (md+): everything collapses back to the original
            single `items-end` row via flex order + nowrap. */}
        <div className="flex flex-wrap items-end gap-2 md:flex-nowrap">
          {/* Utility bar — attachments / canned responses / schedule */}
          <div className="order-2 flex items-center gap-2 md:order-1 md:items-end">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={!canSend || sendMutation.isPending || scheduleMutation.isPending || attachments.length >= ATTACHMENT_MAX_COUNT}
            title="Attach a file"
            data-testid="button-add-reply-attachment"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <CannedResponsePicker
            onInsert={insertCannedResponse}
            memberFirstName={memberName.split(" ")[0]}
            memberFullName={memberName}
            propertyName={propertyName}
            propertyFullAddress={memberPropertyAddress}
            propertyShortAddress={memberPropertyShortAddress}
            roomNumber={memberRoomNumber}
            memberMoveInDate={memberDetailQuery.data?.moveInDate ?? null}
            memberLeaseEndDate={memberDetailQuery.data?.moveOutDate ?? null}
            weeklyRent={memberDetailQuery.data?.weeklyRate ?? null}
            hostName={senderName || null}
            disabled={!canSend}
          />
          {/* Schedule for later */}
          <Popover open={scheduleOpen} onOpenChange={setScheduleOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={sendDisabled}
                title="Schedule for later"
                data-testid="button-schedule-send"
                data-tour="messages-schedule"
              >
                <Clock className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-4" align="end" data-testid="popover-schedule-datepicker">
              <div className="mb-3 text-sm font-medium text-gray-900">Schedule for later</div>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-gray-600">Date</Label>
                  <Input
                    type="date"
                    value={scheduleDate}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => { setScheduleDate(e.target.value); setScheduleError(""); }}
                    className="mt-1"
                    data-testid="input-schedule-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Time ({Intl.DateTimeFormat().resolvedOptions().timeZone})</Label>
                  <Input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => { setScheduleTime(e.target.value); setScheduleError(""); }}
                    className="mt-1"
                    data-testid="input-schedule-time"
                  />
                </div>
                {scheduleError && (
                  <p className="text-xs text-red-500" data-testid="text-schedule-error">{scheduleError}</p>
                )}
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setScheduleOpen(false); setScheduleError(""); }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleScheduleConfirm}
                  disabled={scheduleMutation.isPending}
                  data-testid="button-schedule-confirm"
                >
                  {scheduleMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Schedule
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          </div>
          {/* Message textarea — full width on its own line on mobile */}
          <div className="relative order-1 w-full md:order-2 md:w-auto md:flex-1">
            <Textarea
              ref={draftTextareaRef}
              placeholder={canSend ? "Type a message… (drag files in to attach)" : "You don't have permission to send messages."}
              value={draft}
              onChange={handleDraftChange}
              onKeyUp={(e) => {
                const el = e.currentTarget;
                recomputeTriggerToken(el.value, el.selectionStart ?? el.value.length);
              }}
              onClick={(e) => {
                const el = e.currentTarget;
                recomputeTriggerToken(el.value, el.selectionStart ?? el.value.length);
              }}
              onBlur={() => {
                // Defer so a click on a menu item can fire before we close.
                setTimeout(() => setTriggerToken(null), 150);
              }}
              onKeyDown={(e) => {
                if (triggerToken && triggerMatches.length > 0) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setTriggerHighlight((h) => (h + 1) % triggerMatches.length);
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setTriggerHighlight((h) => (h - 1 + triggerMatches.length) % triggerMatches.length);
                    return;
                  }
                  if (e.key === "Enter" && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                    e.preventDefault();
                    pickCannedFromTrigger(triggerMatches[triggerHighlight]);
                    return;
                  }
                  if (e.key === "Tab") {
                    e.preventDefault();
                    pickCannedFromTrigger(triggerMatches[triggerHighlight]);
                    return;
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setTriggerToken(null);
                    return;
                  }
                }
                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                  e.preventDefault();
                  if (!sendDisabled) {
                    const typists = presencePeers.filter((p) => p.state === "typing");
                    if (typists.length > 0) {
                      setCollisionWarningTypists(typists);
                      setCollisionWarningOpen(true);
                    } else {
                      requestSend();
                    }
                  }
                }
              }}
              disabled={!canSend || sendMutation.isPending || scheduleMutation.isPending || cleaningConfirmation.open || isImproving}
              className="min-h-[120px] w-full"
              data-testid="input-message-text"
            />
            {triggerToken && triggerMatches.length > 0 && (
              <div
                className="absolute bottom-full left-0 right-0 mb-1 z-30 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
                data-testid="popover-canned-inline"
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className="border-b border-gray-100 px-3 py-1.5 text-[11px] uppercase tracking-wide text-gray-500">
                  Canned responses · {cannedTrigger}{triggerToken.query}
                </div>
                {triggerMatches.map((r, idx) => (
                  <button
                    key={r.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickCannedFromTrigger(r);
                    }}
                    onMouseEnter={() => setTriggerHighlight(idx)}
                    className={`w-full text-left px-3 py-2 border-b border-gray-50 last:border-0 transition-colors ${
                      idx === triggerHighlight ? "bg-blue-50" : "hover:bg-gray-50"
                    }`}
                    data-testid={`canned-inline-option-${r.id}`}
                  >
                    <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{r.body}</p>
                  </button>
                ))}
                <div className="border-t border-gray-100 px-3 py-1 text-[11px] text-gray-400">
                  ↑↓ to navigate · Enter to insert · Esc to dismiss
                </div>
              </div>
            )}
          </div>
          {/* Action row — AI assist / undo / clear / send.
              On mobile this is pushed to the right of the utility bar (same
              wrapped line); on desktop it flows to the end of the single row. */}
          <div className="order-3 ml-auto flex flex-wrap items-center justify-end gap-2 md:order-3 md:ml-0 md:flex-nowrap md:items-end">
          {/* AI assist button — switches between Suggest (empty draft) and Improve (has text) */}
          {canSend && aiDraftingAllowed && !isSuggesting && !isImproving && draft.trim().length === 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-purple-700 border-purple-300 hover:bg-purple-50 hover:text-purple-800 gap-1.5 shrink-0"
              title="Suggest a reply using AI + your knowledge base"
              data-testid="button-suggest-ai"
              onClick={handleAiAssist}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Suggest
            </Button>
          )}
          {canSend && aiDraftingAllowed && !isSuggesting && !isImproving && draft.trim().length > 0 && (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white gap-1.5 shrink-0"
              title="Rewrite this draft in the company house voice using AI"
              data-testid="button-improve-ai"
              onClick={handleAiAssist}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Improve with AI
            </Button>
          )}
          {(isSuggesting || isImproving) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled
              className="text-purple-400 border-purple-200 gap-1.5 shrink-0"
              data-testid={isSuggesting ? "button-suggest-ai-thinking" : "button-improve-ai-thinking"}
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Thinking…
            </Button>
          )}
          {/* Undo improve — available briefly after an improvement is applied, until the user types */}
          {preImproveText !== null && !isImproving && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-purple-700 hover:text-purple-900 hover:bg-purple-50 px-2 shrink-0"
              title="Undo AI improvement and restore your original text"
              data-testid="button-undo-improve-ai"
              onClick={() => {
                const prev = preImproveText;
                setPreImproveText(null);
                setDraft(prev);
                scheduleAutosave(prev);
              }}
            >
              Undo
            </Button>
          )}
          {draft.trim().length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive px-2"
              title="Clear draft"
              data-testid="button-clear-draft"
              disabled={isImproving}
              onClick={() => {
                setDraft("");
                clearGenRef.current += 1;
                if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
                deleteDraftMutation.mutate();
              }}
            >
              Clear
            </Button>
          )}
          <Button
            disabled={sendDisabled}
            onClick={() => {
              const typists = presencePeers.filter((p) => p.state === "typing");
              if (typists.length > 0) {
                setCollisionWarningTypists(typists);
                setCollisionWarningOpen(true);
              } else {
                requestSend();
              }
            }}
            title={isCleaningContext ? "Preview cleaning message (Ctrl+Enter / Cmd+Enter)" : "Send (Ctrl+Enter / Cmd+Enter on Mac)"}
            data-testid="button-send-message"
          >
            {isCleaningContext ? "Preview message" : sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
          </div>
        </div>
        </>
        )}
      </div>
      {isCleaningContext && <>
        {cleaningConfirmation.dialog}
        <CleaningNotificationHistory propertyId={cleaningProperty!} />
      </>}
      {/* Collision warning — non-blocking "someone is typing" notice shown when
          Send is clicked while a peer is actively typing. Default action sends. */}
      <Dialog
        open={collisionWarningOpen}
        onOpenChange={(open) => {
          if (sendMutation.isPending) return;
          setCollisionWarningOpen(open);
        }}
      >
        <DialogContent
          className="max-w-sm"
          data-testid="dialog-collision-warning"
          onEscapeKeyDown={(e) => { if (sendMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (sendMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (sendMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Someone is also replying</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {collisionWarningTypists.length === 1
              ? `${String(collisionWarningTypists[0].name ?? "").split(" ")[0]} is currently typing a reply to this member. Send your message anyway?`
              : `${collisionWarningTypists.map((p) => String(p.name ?? "").split(" ")[0]).join(" and ")} are currently typing replies to this member. Send your message anyway?`}
          </p>
          <DialogFooter className="gap-2 flex-row justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCollisionWarningOpen(false)}
              data-testid="button-collision-cancel"
            >
              Wait
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setCollisionWarningOpen(false);
                requestSend();
              }}
              data-testid="button-collision-send-anyway"
            >
              <Send className="h-3.5 w-3.5 mr-1" />
              Send anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Task #2987 — Post-reply "Mark as resolved?" prompt */}
      <Dialog
        open={resolvePromptOpen}
        onOpenChange={(open) => {
          if (resolveAllPostSendMutation.isPending) return;
          setResolvePromptOpen(open);
        }}
      >
        <DialogContent
          onEscapeKeyDown={(e) => { if (resolveAllPostSendMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (resolveAllPostSendMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (resolveAllPostSendMutation.isPending) e.preventDefault(); }}
          className="max-w-sm"
          data-testid="dialog-resolve-prompt"
        >
          <DialogHeader>
            <DialogTitle>
              {resolvePromptCount === 1
                ? "Mark as resolved?"
                : `Mark all ${resolvePromptCount} messages from ${memberName} as resolved?`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {resolvePromptCount === 1
              ? `There is 1 unresolved message from ${memberName}. Mark it as resolved now that you've replied?`
              : `You've replied to ${memberName}. Resolve all ${resolvePromptCount} open messages to clear the queue?`}
          </p>
          <DialogFooter className="gap-2 flex-row justify-end">
            <Button
              variant="outline"
              size="sm"
              disabled={resolveAllPostSendMutation.isPending}
              onClick={() => setResolvePromptOpen(false)}
              data-testid="button-resolve-prompt-skip"
            >
              Not yet
            </Button>
            <Button
              size="sm"
              disabled={resolveAllPostSendMutation.isPending}
              onClick={() => resolveAllPostSendMutation.mutate()}
              data-testid="button-resolve-prompt-confirm"
            >
              {resolveAllPostSendMutation.isPending
                ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                : <Check className="h-3 w-3 mr-1" />}
              Yes, resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Messages Auto Draft — confirm replacing typed text with a draft. */}
      <Dialog open={autoDraftReplaceOpen} onOpenChange={(open) => { if (!open) settleAutoDraftReplace(false); }}>
        <DialogContent className="max-w-sm" data-testid="dialog-auto-draft-replace">
          <DialogHeader>
            <DialogTitle>Replace your reply?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            The reply box already has text. Replace it with the auto-draft? The auto-draft will stop its timer and wait for you to send it.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => settleAutoDraftReplace(false)} data-testid="button-auto-draft-replace-keep">
              Keep my text
            </Button>
            <Button size="sm" onClick={() => settleAutoDraftReplace(true)} data-testid="button-auto-draft-replace-confirm">
              Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AutoDraftComposerContext.Provider>
  );
}
